// @ts-strict-check
import React, { useMemo, useRef, useState } from 'react';
import { Check, Eye, EyeOff } from 'lucide-react';
import {
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ColumnInfoTooltip,
  HorizontalTableShell,
  ToggleButton,
} from '../ui';
import ChartControls from '../ui/ChartControls';
import { useMarketPulseData } from '../../hooks/useClubSections';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { TimePeriod } from '../../types/api';
import { IncompleteMonthInfo } from '../../utils/clubGrowthMetrics';
import {
  METRICS,
  MetricKey,
  PulseRow,
  TOLERANCE_PP,
  ComparisonStatus,
  MARKET_PULSE_COVERAGE,
  MARKET_PULSE_DASHBOARD,
  MARKET_PULSE_DASHBOARD_SOURCE,
  MARKET_PULSE_LABELS,
  MARKET_PULSE_TABLE,
  MARKET_PULSE_YOY_TABLE,
  MARKET_PULSE_TABLE_MONTH_TOOLTIP,
  MARKET_PULSE_TABLE_ID,
  MARKET_PULSE_YOY_TABLE_ID,
  formatTableDiffTitle,
} from './marketPulseConstants';
import {
  fmtPct,
  fmtUnit,
  getStatus,
  STATUS_CONFIG,
} from './marketPulseFormatting';
import {
  computeMarketPulseComparisonData,
  computeMarketPulseYoYComparisonData,
  hasMarketPulseYoYData,
  MarketPulseComparisonData,
} from './marketPulseComparisonData';

export type MarketPulseTableVariant = 'mom' | 'yoy';

const Num: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span dir="ltr" className={`inline-block tabular-nums ${className ?? ''}`}>{children}</span>
);

const hasNumericValue = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

const CompleteMonthCheck: React.FC = () => (
  <span
    className="inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0"
    style={{
      backgroundColor: 'color-mix(in srgb, var(--status-success) 18%, transparent)',
      color: 'var(--status-success)',
    }}
    title={MARKET_PULSE_COVERAGE.completeTitle}
    aria-label={MARKET_PULSE_COVERAGE.completeBadgeAria}
  >
    <Check className="w-2.5 h-2.5" strokeWidth={3} aria-hidden="true" />
  </span>
);

const BlurredValue: React.FC<{
  children: React.ReactNode;
  className?: string;
  warningTitle: string;
  forceRevealed?: boolean;
}> = ({ children, className, warningTitle, forceRevealed = false }) => {
  const [revealed, setRevealed] = useState(forceRevealed);
  const prevForceRevealed = useRef(forceRevealed);

  if (prevForceRevealed.current !== forceRevealed) {
    prevForceRevealed.current = forceRevealed;
    setRevealed(forceRevealed);
  }

  const EyeIcon = revealed ? EyeOff : Eye;

  return (
    <span
      className={`group/blur relative inline-grid place-items-center mx-auto ${className ?? ''}`}
    >
      <span
        className={`col-start-1 row-start-1 inline-block tabular-nums leading-none transition-[filter,opacity] duration-200 ${
          revealed ? '' : 'select-none'
        }`}
        style={{
          filter: revealed ? 'none' : 'blur(2px)',
          opacity: revealed ? 1 : 0.92,
        }}
        aria-hidden={!revealed}
      >
        {children}
      </span>
      <button
        type="button"
        onClick={() => setRevealed((prev) => !prev)}
        className={`col-start-1 row-start-1 z-[1] inline-flex items-center justify-center w-full h-full min-w-[1.75rem] min-h-[1.75rem] rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-orange ${
          revealed
            ? 'text-text-tertiary opacity-0 group-hover/blur:opacity-90 hover:bg-bg-secondary/35'
            : 'text-text-tertiary hover:text-text-secondary hover:bg-bg-secondary/40'
        }`}
        title={revealed ? `${warningTitle} — ${MARKET_PULSE_COVERAGE.blurHideHint}` : MARKET_PULSE_COVERAGE.blurRevealHint}
        aria-pressed={revealed}
        aria-label={revealed ? MARKET_PULSE_COVERAGE.blurHideHint : MARKET_PULSE_COVERAGE.blurRevealHint}
      >
        <EyeIcon className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
      </button>
    </span>
  );
};

const CoverageBadge: React.FC<{
  variant: 'limited' | 'estimated';
  monthInfo: IncompleteMonthInfo;
}> = ({ variant, monthInfo }) => {
  const isEstimated = variant === 'estimated';

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-medium leading-tight text-text-secondary border"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: isEstimated ? 'var(--intent-warning-border)' : 'var(--border-secondary)',
        borderStyle: isEstimated ? 'dashed' : 'solid',
      }}
      title={
        isEstimated
          ? MARKET_PULSE_COVERAGE.estimatedTitle
          : MARKET_PULSE_COVERAGE.limitedTitle
      }
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ backgroundColor: 'var(--accent-orange)' }}
        aria-hidden="true"
      />
      <span>{isEstimated ? MARKET_PULSE_COVERAGE.estimatedBadge : MARKET_PULSE_COVERAGE.limitedBadge}</span>
      <span className="text-text-tertiary tabular-nums">
        <Num>{monthInfo.orderDays}</Num>/<Num>{monthInfo.fullDays}</Num>
      </span>
    </span>
  );
};

const UNRELIABLE_PAIR_BASE: React.CSSProperties = {
  backgroundColor: 'color-mix(in srgb, var(--accent-orange) 12%, var(--bg-card))',
};

const getUnreliableBranchStyle = (): React.CSSProperties => UNRELIABLE_PAIR_BASE;

const getUnreliableDiffStyle = (showMarketColumn: boolean): React.CSSProperties =>
  showMarketColumn
    ? {
        ...UNRELIABLE_PAIR_BASE,
        borderInlineEnd: '2px solid var(--accent-orange)',
      }
    : UNRELIABLE_PAIR_BASE;

const TABLE_QUALITY_STYLE = {
  monthLimited: {
    backgroundColor: 'var(--bg-secondary)',
    borderInlineEnd: '3px solid var(--accent-orange)',
  } satisfies React.CSSProperties,
  monthEstimated: {
    backgroundColor: 'var(--bg-secondary)',
    borderInlineEnd: '3px solid var(--accent-orange)',
    outline: '1px dashed var(--intent-warning-border)',
    outlineOffset: '-1px',
  } satisfies React.CSSProperties,
} as const;

const CoverageLegendSwatchComplete: React.FC = () => (
  <span
    className="inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0"
    style={{
      backgroundColor: 'color-mix(in srgb, var(--status-success) 18%, transparent)',
      color: 'var(--status-success)',
    }}
    aria-hidden="true"
  >
    <Check className="w-2.5 h-2.5" strokeWidth={3} />
  </span>
);

const CoverageLegendSwatchMonth: React.FC = () => (
  <span
    className="w-4 h-3 rounded-sm shrink-0"
    style={{
      backgroundColor: 'var(--bg-secondary)',
      borderInlineEnd: '3px solid var(--accent-orange)',
    }}
    aria-hidden="true"
  />
);

const CoverageLegendSwatchPair: React.FC = () => (
  <span className="inline-flex h-3 shrink-0 rounded-sm overflow-hidden" aria-hidden="true">
    <span className="w-2.5" style={UNRELIABLE_PAIR_BASE} />
    <span
      className="w-2.5"
      style={{
        ...UNRELIABLE_PAIR_BASE,
        borderInlineEnd: '2px solid var(--accent-orange)',
      }}
    />
  </span>
);

const CoverageLegendSwatchEstimated: React.FC = () => (
  <span
    className="w-4 h-3 rounded-sm shrink-0 border border-dashed"
    style={{
      backgroundColor: 'var(--bg-secondary)',
      borderColor: 'var(--intent-warning-border)',
    }}
    aria-hidden="true"
  />
);

const LegendChip: React.FC<{
  label: string;
  tone?: 'success' | 'neutral' | 'error' | 'warning';
  swatch?: React.ReactNode;
}> = ({ label, tone = 'neutral', swatch }) => {
  const toneClass =
    tone === 'success'
      ? 'bg-status-success'
      : tone === 'error'
        ? 'bg-status-error'
        : tone === 'warning'
          ? 'bg-status-warning'
          : 'bg-bg-tertiary border border-border-primary';

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
      {swatch ?? <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${toneClass}`} aria-hidden="true" />}
      {label}
    </span>
  );
};

const ExtrapolationBadge: React.FC<{ monthInfo: IncompleteMonthInfo }> = ({ monthInfo }) => (
  <CoverageBadge variant="estimated" monthInfo={monthInfo} />
);

const IncompleteDaysBadge: React.FC<{ monthInfo: IncompleteMonthInfo }> = ({ monthInfo }) => (
  <CoverageBadge variant="limited" monthInfo={monthInfo} />
);

const DiffPill: React.FC<{
  status: ComparisonStatus;
  text: string;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
}> = ({ status, text, title, size = 'sm' }) => {
  const cfg = STATUS_CONFIG[status];
  const sizeClass =
    size === 'lg'
      ? 'px-4 py-2.5 text-xl rounded-xl shadow-sm border-2'
      : size === 'md'
        ? 'px-3 py-1.5 text-sm rounded-lg shadow-sm border-2'
        : 'px-2 py-1 rounded-full text-xs';
  const iconClass = size === 'lg' ? 'text-lg' : size === 'md' ? 'text-base' : 'text-xs';
  const numClass = size === 'lg' ? 'text-2xl font-extrabold' : size === 'md' ? 'text-xl font-black' : '';
  return (
    <div
      className={`flex items-center justify-center gap-1 font-bold leading-none mx-auto w-fit border ${sizeClass}`}
      style={{ backgroundColor: cfg.bg, color: cfg.color, borderColor: cfg.border }}
      title={title}
    >
      <span className={`${iconClass} leading-none`} aria-hidden="true">
        {cfg.icon}
      </span>
      <Num className={`leading-none ${numClass}`}>{text}</Num>
    </div>
  );
};

interface MarketPulseTableSectionProps {
  branchId?: number;
  customTimeRange?: TimePeriod;
  extrapolateToFullMonth?: boolean;
  comparisonData?: MarketPulseComparisonData;
  variant?: MarketPulseTableVariant;
  className?: string;
}

const MarketPulseTableSection: React.FC<MarketPulseTableSectionProps> = ({
  branchId = 0,
  customTimeRange,
  extrapolateToFullMonth = false,
  comparisonData: externalComparisonData,
  variant = 'mom',
  className = '',
}) => {
  const isYoY = variant === 'yoy';
  const tableConfig = isYoY ? MARKET_PULSE_YOY_TABLE : MARKET_PULSE_TABLE;
  const tableId = isYoY ? MARKET_PULSE_YOY_TABLE_ID : MARKET_PULSE_TABLE_ID;
  const chartGuide = isYoY ? CHART_GUIDES.marketPulseYoYTable : CHART_GUIDES.marketPulseTable;
  const unreliableMetricTitle = isYoY
    ? MARKET_PULSE_COVERAGE.unreliableYoYMetricTitle
    : MARKET_PULSE_COVERAGE.unreliableMetricTitle;
  const contentRef = useRef<HTMLElement>(null);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();

  const [hiddenMetrics, setHiddenMetrics] = useState<MetricKey[]>([]);
  const [showMarketColumn, setShowMarketColumn] = useState<boolean>(true);
  const [revealAllBlurred, setRevealAllBlurred] = useState(false);

  const toggleMetric = (key: MetricKey) =>
    setHiddenMetrics((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const shouldFetch = !externalComparisonData;
  const { data, isLoading, error, refetch } = useMarketPulseData(
    shouldFetch ? branchId : undefined,
    customTimeRange,
  );

  const comparisonData = useMemo(() => {
    if (externalComparisonData) return externalComparisonData;
    if (!data) return null;
    const branchAll = (data.branchData as PulseRow[]) || [];
    const marketAll = (data.allRestaurantsData as PulseRow[]) || [];
    const marketPartialLatest = (data.allRestaurantsPartialLatestData as PulseRow[]) || [];
    const marketYoYAll = data.allRestaurantsYoYData ?? [];
    const baseMonths = data.baseMonths ?? branchAll.length;
    return isYoY
      ? computeMarketPulseYoYComparisonData(
          branchAll,
          marketYoYAll,
          baseMonths,
          extrapolateToFullMonth,
          marketPartialLatest,
          marketAll,
        )
      : computeMarketPulseComparisonData(
          branchAll,
          marketAll,
          baseMonths,
          extrapolateToFullMonth,
          marketPartialLatest,
        );
  }, [externalComparisonData, data, extrapolateToFullMonth, isYoY]);

  const activeMetrics = METRICS.filter((m) => !hiddenMetrics.includes(m.key));

  const hasBlurredValues = useMemo(() => {
    if (!comparisonData || comparisonData.extrapolate) return false;
    const visibleMetrics = METRICS.filter((m) => !hiddenMetrics.includes(m.key));
    return comparisonData.tableRows.some((row) => {
      if (!row.branchGrowthUnreliable) return false;
      return visibleMetrics.some((m) => {
        const cell = row.cells[m.key];
        return hasNumericValue(cell.branch) || hasNumericValue(cell.diff);
      });
    });
  }, [comparisonData, hiddenMetrics]);

  if (!hasBlurredValues && revealAllBlurred) {
    setRevealAllBlurred(false);
  }

  if (shouldFetch && isLoading) {
    return (
      <div className={`card-base card-padding flex justify-center py-12 ${className}`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (shouldFetch && error) {
    return (
      <div className={`card-base card-padding ${className}`}>
        <ErrorMessage message={MARKET_PULSE_DASHBOARD.errorFetch} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isYoY && !externalComparisonData) {
    const branchAll = (data?.branchData as PulseRow[]) || [];
    if (!hasMarketPulseYoYData(branchAll, comparisonData)) {
      return null;
    }
  }

  if (!comparisonData || comparisonData.tableRows.length === 0) {
    return (
      <div className={`card-base card-padding ${className}`}>
        <EmptyState message={MARKET_PULSE_DASHBOARD.emptyNoData} />
      </div>
    );
  }

  return (
    <section ref={contentRef} className={`card-base card-padding space-y-5 ${className}`}>
      <ChartControls
        componentId={tableId}
        componentTitle={tableConfig.title}
        componentType="table"
        dashboardSource={MARKET_PULSE_DASHBOARD_SOURCE}
        showClubShortkey
        clubShortkeyLabel={MARKET_PULSE_DASHBOARD.clubShortkey}
        onInfo={() => openGuide(chartGuide)}
        onFavorite={() => handleFavoriteToggle({
          componentId: tableId,
          componentTitle: tableConfig.title,
          dashboardSource: MARKET_PULSE_DASHBOARD_SOURCE,
          componentType: 'table',
          description: tableConfig.favoriteDescription,
          props: { branchId, extrapolateToFullMonth, variant },
          gridLayout: { containerClass: 'grid grid-cols-1 gap-6' },
        })}
        isFavorited={isFavorited(tableId)}
        chartRef={contentRef}
        className="mb-2"
      />

      <div className="flex flex-col items-center gap-4">
        <h3 className="title-card !mb-0 !text-center">{tableConfig.title}</h3>
        <p className="text-xs text-text-tertiary text-center max-w-lg leading-relaxed">
          {tableConfig.subtitle}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {METRICS.map((m) => {
              const active = !hiddenMetrics.includes(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => toggleMetric(m.key)}
                  aria-pressed={active}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-base border ${
                    active
                      ? 'bg-primary-600 text-theme-inverse border-primary-600 shadow-sm'
                      : 'bg-theme-secondary text-theme-secondary border-border-primary hover:text-theme-primary'
                  }`}
                >
                  {m.shortLabel}
                </button>
              );
            })}
          </div>
          <ToggleButton
            activeValue={showMarketColumn ? 'show' : 'hide'}
            onChange={(v) => setShowMarketColumn(v === 'show')}
            options={[
              { value: 'show', label: tableConfig.toggleShowMarket },
              { value: 'hide', label: tableConfig.toggleDiffOnly },
            ]}
          />
          {hasBlurredValues && (
            <button
              type="button"
              onClick={() => setRevealAllBlurred((prev) => !prev)}
              aria-pressed={revealAllBlurred}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-base border ${
                revealAllBlurred
                  ? 'bg-primary-600 text-theme-inverse border-primary-600 shadow-sm'
                  : 'bg-theme-secondary text-theme-secondary border-border-primary hover:text-theme-primary'
              }`}
              title={
                revealAllBlurred
                  ? MARKET_PULSE_COVERAGE.hideAllLabel
                  : MARKET_PULSE_COVERAGE.revealAllLabel
              }
            >
              {revealAllBlurred ? (
                <EyeOff className="w-3.5 h-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
              ) : (
                <Eye className="w-3.5 h-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
              )}
              {revealAllBlurred
                ? MARKET_PULSE_COVERAGE.hideAllLabel
                : MARKET_PULSE_COVERAGE.revealAllLabel}
            </button>
          )}
        </div>
      </div>

      {activeMetrics.length === 0 ? (
        <p className="text-sm text-text-secondary text-center py-8">{tableConfig.selectAtLeastOne}</p>
      ) : (
        <HorizontalTableShell
          minWidth={Math.max(720, (1 + activeMetrics.length * (showMarketColumn ? 3 : 2)) * 110)}
          scrollHint={tableConfig.scrollHint}
        >
          <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
            <thead>
              <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{ borderColor: 'var(--border-secondary)' }}>
                <th
                  rowSpan={2}
                  className="ps-5 pe-3 py-4 text-right text-xs font-bold border-l-2 last:border-l-0"
                  style={{ color: 'var(--text-primary)', borderColor: 'var(--border-secondary)' }}
                >
                  <div className="inline-flex items-center gap-1.5">
                    <span className="font-semibold text-sm leading-none">{MARKET_PULSE_LABELS.month}</span>
                    <ColumnInfoTooltip text={MARKET_PULSE_TABLE_MONTH_TOOLTIP} />
                  </div>
                </th>
                {activeMetrics.map((m) => (
                  <th
                    key={m.key}
                    colSpan={showMarketColumn ? 3 : 2}
                    className="px-3 py-3 text-center text-xs font-bold border-l-2 last:border-l-0"
                    style={{ color: 'var(--text-primary)', borderColor: 'var(--border-secondary)' }}
                  >
                    <div className="inline-flex items-center justify-center gap-1.5">
                      <span className="font-semibold text-sm leading-none">{m.label}</span>
                      <ColumnInfoTooltip text={m.tooltip} />
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{ borderColor: 'var(--border-secondary)' }}>
                {activeMetrics.map((m) => (
                  <React.Fragment key={m.key}>
                    <th
                      className="px-3 py-2.5 text-center text-[11px] font-semibold border-l last:border-l-0"
                      style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-primary)' }}
                    >
                      {MARKET_PULSE_LABELS.branch}
                    </th>
                    <th
                      className={`px-3 py-2.5 text-center text-[11px] font-semibold last:border-l-0 ${
                        showMarketColumn ? 'border-l' : 'border-l-2'
                      }`}
                      style={{
                        color: 'var(--text-secondary)',
                        borderColor: showMarketColumn ? 'var(--border-primary)' : 'var(--border-secondary)',
                      }}
                    >
                      {MARKET_PULSE_LABELS.diff}
                    </th>
                    {showMarketColumn && (
                      <th
                        className="px-3 py-2.5 text-center text-[11px] font-semibold border-l-2 last:border-l-0"
                        style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-secondary)' }}
                      >
                        {MARKET_PULSE_LABELS.market}
                      </th>
                    )}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonData.tableRows.map((row, rowIndex) => (
                <tr
                  key={row.key}
                  className={`border-b ${
                    rowIndex % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800'
                  }`}
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  <td
                    className="ps-5 pe-3 py-4 text-right text-sm font-bold border-l-2 last:border-l-0 text-text-primary"
                    style={{
                      borderColor: 'var(--border-secondary)',
                      ...(row.isExtrapolated
                        ? TABLE_QUALITY_STYLE.monthEstimated
                        : row.isIncomplete
                          ? TABLE_QUALITY_STYLE.monthLimited
                          : { backgroundColor: 'var(--bg-tertiary)' }),
                    }}
                  >
                    <span className="inline-flex flex-col items-start gap-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        {row.showCompleteCheck ? <CompleteMonthCheck /> : null}
                        <span>{row.label}</span>
                      </span>
                      {row.isExtrapolated ? (
                        <ExtrapolationBadge monthInfo={row.monthInfo} />
                      ) : row.isIncomplete ? (
                        <IncompleteDaysBadge monthInfo={row.monthInfo} />
                      ) : null}
                    </span>
                  </td>
                  {activeMetrics.map((m) => {
                    const cell = row.cells[m.key];
                    const status = getStatus(cell.diff);
                    const growthUnreliable =
                      row.branchGrowthUnreliable && !comparisonData.extrapolate;
                    const branchHasValue = hasNumericValue(cell.branch);
                    const diffHasValue = hasNumericValue(cell.diff);
                    const showBranchBlur = growthUnreliable && branchHasValue;
                    const showDiffBlur = growthUnreliable && diffHasValue;
                    return (
                      <React.Fragment key={m.key}>
                        <td
                          className={`px-3 py-4 text-center text-sm font-semibold border-l last:border-l-0 ${
                            showBranchBlur ? 'text-text-secondary' : 'text-text-primary'
                          }`}
                          style={{
                            borderColor: 'var(--border-primary)',
                            ...(showBranchBlur ? getUnreliableBranchStyle() : {}),
                          }}
                          title={showBranchBlur ? unreliableMetricTitle : undefined}
                        >
                          {showBranchBlur ? (
                            <BlurredValue
                              warningTitle={unreliableMetricTitle}
                              forceRevealed={revealAllBlurred}
                            >
                              <Num className="font-semibold">{fmtPct(cell.branch)}</Num>
                            </BlurredValue>
                          ) : (
                            <Num className="font-semibold">{fmtPct(cell.branch)}</Num>
                          )}
                        </td>
                        <td
                          className={`px-3 py-4 text-center last:border-l-0 ${
                            showMarketColumn ? 'border-l' : 'border-l-2'
                          }`}
                          style={{
                            borderColor: showMarketColumn ? 'var(--border-primary)' : 'var(--border-secondary)',
                            ...(showDiffBlur ? getUnreliableDiffStyle(showMarketColumn) : {}),
                          }}
                          title={showDiffBlur ? unreliableMetricTitle : undefined}
                        >
                          {showDiffBlur ? (
                            <BlurredValue
                              warningTitle={unreliableMetricTitle}
                              forceRevealed={revealAllBlurred}
                            >
                              <Num className="text-sm font-medium text-text-secondary">{fmtUnit(cell.diff)}</Num>
                            </BlurredValue>
                          ) : diffHasValue ? (
                            <DiffPill
                              status={status}
                              text={fmtUnit(cell.diff)}
                              title={formatTableDiffTitle(fmtUnit(cell.diff))}
                            />
                          ) : (
                            <Num className="text-sm font-medium text-text-secondary">{fmtUnit(cell.diff)}</Num>
                          )}
                        </td>
                        {showMarketColumn && (
                          <td
                            className="px-3 py-4 text-center text-sm font-medium border-l-2 last:border-l-0 text-text-secondary"
                            style={{ borderColor: 'var(--border-secondary)' }}
                          >
                            <Num className="font-medium">{fmtPct(cell.market)}</Num>
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </HorizontalTableShell>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] md:text-xs border-t border-border-primary pt-3">
        <LegendChip tone="success" label={tableConfig.legendAhead} />
        <LegendChip tone="neutral" label={tableConfig.legendEqual} />
        <LegendChip tone="error" label={tableConfig.legendBehind} />
        <LegendChip swatch={<CoverageLegendSwatchComplete />} label={tableConfig.legendBranchComplete} />
        {comparisonData.extrapolate && comparisonData.hasExtrapolatedMonths ? (
          <LegendChip swatch={<CoverageLegendSwatchEstimated />} label={tableConfig.legendEstimated} />
        ) : comparisonData.hasIncompleteMonths ? (
          <>
            <LegendChip swatch={<CoverageLegendSwatchMonth />} label={tableConfig.legendBranchIncomplete} />
            <LegendChip
              swatch={<CoverageLegendSwatchPair />}
              label={tableConfig.legendUnreliablePair}
            />
            <span className="text-text-tertiary">{tableConfig.legendMarketReliable}</span>
          </>
        ) : null}
        {isYoY && 'marketPairedNote' in tableConfig && (
          <span className="text-text-tertiary leading-relaxed">{tableConfig.marketPairedNote}</span>
        )}
        <span className="w-full md:w-auto md:mr-auto text-text-tertiary leading-relaxed">
          {tableConfig.growthFormula} <Num>{TOLERANCE_PP.toFixed(2)}</Num>
        </span>
      </div>


    </section>
  );
};

export default MarketPulseTableSection;
